"use client"

import { addToCart } from "@lib/data/cart"
import { Button, clx } from "@medusajs/ui"
import ChevronDown from "@modules/common/icons/chevron-down"
import { useParams } from "next/navigation"
import { useMemo, useState } from "react"

type WasteOption = {
  label: string
  factor: number
}

const WASTE_OPTIONS: WasteOption[] = [
  { label: "Straight +10%", factor: 0.1 },
  { label: "Diagonal / Herringbone +15%", factor: 0.15 },
  { label: "Large format +20%", factor: 0.2 },
]

type CoverageCalculatorProps = {
  // The by-the-box variant id to add to cart.
  boxVariantId?: string
  // Square feet covered by a single box.
  sqftPerBox?: number
}

export default function CoverageCalculator({
  boxVariantId,
  sqftPerBox,
}: CoverageCalculatorProps) {
  const countryCode = useParams().countryCode as string

  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<"area" | "dimensions">("area")
  const [area, setArea] = useState("")
  const [length, setLength] = useState("")
  const [width, setWidth] = useState("")
  const [wasteFactor, setWasteFactor] = useState(WASTE_OPTIONS[0].factor)
  const [isAdding, setIsAdding] = useState(false)

  const baseArea = useMemo(() => {
    if (mode === "area") {
      const a = Number(area)
      return Number.isFinite(a) && a > 0 ? a : 0
    }
    const l = Number(length)
    const w = Number(width)
    if (Number.isFinite(l) && l > 0 && Number.isFinite(w) && w > 0) {
      return l * w
    }
    return 0
  }, [mode, area, length, width])

  // Render nothing gracefully if we can't compute boxes.
  const validSqftPerBox =
    !!sqftPerBox && Number.isFinite(sqftPerBox) && sqftPerBox > 0

  const neededSqft = baseArea * (1 + wasteFactor)
  const boxes =
    validSqftPerBox && baseArea > 0
      ? Math.ceil(neededSqft / (sqftPerBox as number))
      : 0
  const coveredSqft = boxes * (sqftPerBox ?? 0)

  const handleAddToCart = async () => {
    if (!boxVariantId || boxes <= 0) return

    setIsAdding(true)

    await addToCart({
      variantId: boxVariantId,
      quantity: boxes,
      countryCode,
    })

    setIsAdding(false)
  }

  // Render nothing gracefully if sqftPerBox is missing/invalid.
  if (!validSqftPerBox) {
    return null
  }

  return (
    <div className="bg-bone border border-line rounded-md text-ink">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
        data-testid="coverage-calculator-toggle"
      >
        <span className="font-serif text-base font-semibold">
          Coverage calculator
        </span>
        <ChevronDown
          className={clx("transition-transform duration-150", {
            "rotate-180": open,
          })}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 flex flex-col gap-y-4 border-t border-line pt-4">
          <div className="flex gap-x-2">
            <button
              type="button"
              onClick={() => setMode("area")}
              className={clx(
                "flex-1 h-9 rounded-md border text-sm transition-colors",
                mode === "area"
                  ? "border-clay text-clay bg-surface"
                  : "border-line text-muted hover:text-ink"
              )}
            >
              By area
            </button>
            <button
              type="button"
              onClick={() => setMode("dimensions")}
              className={clx(
                "flex-1 h-9 rounded-md border text-sm transition-colors",
                mode === "dimensions"
                  ? "border-clay text-clay bg-surface"
                  : "border-line text-muted hover:text-ink"
              )}
            >
              Length x width
            </button>
          </div>

          {mode === "area" ? (
            <label className="flex flex-col gap-y-1 text-sm">
              <span className="text-muted">Area (sq ft)</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                value={area}
                onChange={(e) => setArea(e.target.value)}
                placeholder="e.g. 120"
                className="h-10 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:border-clay"
                data-testid="coverage-area-input"
              />
            </label>
          ) : (
            <div className="flex gap-x-3">
              <label className="flex-1 flex flex-col gap-y-1 text-sm">
                <span className="text-muted">Length (ft)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  placeholder="e.g. 12"
                  className="h-10 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:border-clay"
                  data-testid="coverage-length-input"
                />
              </label>
              <label className="flex-1 flex flex-col gap-y-1 text-sm">
                <span className="text-muted">Width (ft)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="e.g. 10"
                  className="h-10 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:border-clay"
                  data-testid="coverage-width-input"
                />
              </label>
            </div>
          )}

          <label className="flex flex-col gap-y-1 text-sm">
            <span className="text-muted">Waste allowance</span>
            <select
              value={wasteFactor}
              onChange={(e) => setWasteFactor(Number(e.target.value))}
              className="h-10 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:border-clay"
              data-testid="coverage-waste-select"
            >
              {WASTE_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.factor}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          {boxes > 0 ? (
            <div className="flex flex-col gap-y-1">
              <p className="text-clay font-medium" data-testid="coverage-result">
                You&apos;ll need {boxes} {boxes === 1 ? "box" : "boxes"} (covers
                ~{coveredSqft.toFixed(0)} sq ft incl. waste)
              </p>
              <p className="text-sage text-sm">
                We recommend +10% for cuts &amp; waste.
              </p>
            </div>
          ) : (
            <p className="text-muted text-sm">
              Enter your {mode === "area" ? "area" : "dimensions"} to estimate
              boxes.
            </p>
          )}

          {boxVariantId && (
            <Button
              onClick={handleAddToCart}
              disabled={boxes <= 0 || isAdding}
              variant="primary"
              className="w-full h-10 bg-clay hover:bg-clay-deep border-clay"
              isLoading={isAdding}
              data-testid="coverage-add-button"
            >
              {boxes > 0
                ? `Add ${boxes} ${boxes === 1 ? "box" : "boxes"} to cart`
                : "Add to cart"}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
